<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-file-card">
    <!--
      ONE ROW (prototype v3, design-spec §17.4): the name on top, everything
      else on one quiet line, and the buttons straight after the data,
      vertically centred — not flung to the far right where the eye arrives
      last. The TABLE COUNT is the load-bearing number (recovering the
      adjustment table as rows is what makes the arithmetic checks mean
      anything); there is deliberately NO page count — a .docx carries none.
    -->
    <div class="flex flex-wrap items-center gap-x-5 gap-y-3">
      <div class="flex min-w-0 items-center gap-3">
        <span class="pi pi-file shrink-0 text-xl text-blue-500" aria-hidden="true"></span>
        <div class="min-w-0">
          <p class="break-words font-medium leading-tight text-gray-800" data-testid="qc-file-name">
            {{ upload.originalFilename }}
          </p>
          <p class="mt-1 text-xs leading-relaxed text-gray-500" data-testid="qc-file-meta">
            <template v-for="(part, index) in meta" :key="index">
              <span v-if="index > 0" class="mx-0.5 text-gray-300" aria-hidden="true">·</span>
              <strong v-if="part.strong" class="font-semibold text-gray-700">{{ part.text }}</strong>
              <span v-else>{{ part.text }}</span>
            </template>
          </p>
        </div>
      </div>

      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <Button
          v-if="mode === 'ready'"
          :label="$t('messages.evaluation.qualityCheck.file.startNow')"
          icon="pi pi-shield"
          class="w-auto! px-5! py-2.5! text-base! font-semibold!"
          :loading="starting"
          data-testid="qc-start-now"
          @click="emit('start')"
        />
        <Button
          v-else
          :label="$t('messages.evaluation.qualityCheck.file.rerun')"
          icon="pi pi-refresh"
          severity="secondary"
          size="small"
          class="w-auto!"
          :loading="starting"
          data-testid="qc-rerun"
          @click="emit('start')"
        />
        <!-- Deletes the FILE and its text now; the findings stay (§6.3.4). -->
        <Button
          v-tooltip.top="$t('messages.evaluation.qualityCheck.file.removeHint')"
          :label="$t('messages.evaluation.qualityCheck.file.remove')"
          icon="pi pi-trash"
          severity="danger"
          outlined
          size="small"
          class="w-auto!"
          :loading="removing"
          data-testid="qc-file-remove"
          @click="emit('remove')"
        />
      </div>
    </div>

    <!-- Zero tables is a loud warning, not a silent pass (§6.3.2). The check
         may still run — the appraiser decides. -->
    <QcNotice
      v-if="upload.tableCount === 0"
      tone="warn"
      icon="pi-exclamation-triangle"
      class="mt-2.5"
      data-testid="qc-no-tables"
    >
      {{ $t('messages.evaluation.qualityCheck.file.noTables') }}
    </QcNotice>

    <QcBetaNote class="mt-3" :support-email="supportEmail" />
  </div>
</template>

<script setup>
import { format, parseISO } from 'date-fns'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import QcBetaNote from './QcBetaNote.vue'
import QcNotice from './QcNotice.vue'

/**
 * RED-351 rework — the file under check (prototype v3 `fileCard`).
 *
 * `mode: 'ready'` is a file no run has checked yet: the big `Käivita kontroll
 * kohe`. Any other mode is a checked file: the quiet `Käivita kontroll uuesti`.
 * Both only ASK — the cost dialog decides (useReportCheck.requestStart).
 *
 * **Only way 1 (the appraiser's own upload) exists.** Way 2's `koostatud
 * hindamisest` / `Koosta uuesti` wording belongs to a file REdata built, and
 * "üles laetud" would be a lie for it; that path is its own ticket.
 */
const props = defineProps({
  upload: { type: Object, required: true },
  mode: { type: String, default: 'done' },
  supportEmail: { type: String, required: true },
  starting: { type: Boolean, default: false },
  removing: { type: Boolean, default: false },
})

const emit = defineEmits(['start', 'remove'])

const { t } = useI18n()

const integer = value => new Intl.NumberFormat('et-EE').format(Number(value ?? 0))

const megabytes = bytes => `${(Number(bytes ?? 0) / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`

const meta = computed(() => {
  const parts = [{ text: megabytes(props.upload.byteSize) }]
  if (props.upload.uploadedAt) {
    parts.push({
      text: t('messages.evaluation.qualityCheck.file.uploadedAt', { time: format(parseISO(props.upload.uploadedAt), 'HH:mm') }),
    })
  }
  parts.push({ text: t('messages.evaluation.qualityCheck.file.tables', { n: integer(props.upload.tableCount) }), strong: true })
  parts.push({ text: t('messages.evaluation.qualityCheck.file.chars', { n: integer(props.upload.charCount) }), strong: true })
  parts.push({ text: t('messages.evaluation.qualityCheck.file.onlyYou') })
  if (props.upload.deletesAt) {
    // Written out in full, never "2 days left" (§6.3.5).
    const when = parseISO(props.upload.deletesAt)
    parts.push({
      text: t('messages.evaluation.qualityCheck.file.deletes', { date: format(when, 'dd.MM.yyyy'), time: format(when, 'HH:mm') }),
    })
  }
  return parts
})
</script>
